import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useNavigation } from '@react-navigation/native'
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list'
import { Asset } from 'expo-asset'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { throttle } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dimensions, Platform, Text, TouchableOpacity, View } from 'react-native'
import { ms, ScaledSheet } from 'react-native-size-matters'
import { useDispatch, useSelector } from 'react-redux'
import useSWRInfinite from 'swr/infinite'
import { Tag } from '~/types'
import {
  CommunityImageSearchResource,
  CommunityImageSearchResourceCollection,
  SearchEngineImageSearchResource,
} from '~api/types'
import { CommunityImageSearchItem } from '~components/CommunityImageSearchItem'
import Loading from '~components/Loading'
import ModalScreen, { getModalStyleDefinition, ModalHeader } from '~components/ModalScreen'
import RenderError from '~components/RenderError'
import TagsInput from '~components/TagsInput'
import {
  IMAGE_MAX_DIMENSION_ONE_SECTION,
  IMAGE_MAX_DIMENSION_OVER_ONE_SECTION,
} from '~constants/Settings'
import useChatRouteParams from '~hooks/useChatRouteParams'
import useColumns from '~hooks/useColumns'
import useComponentSize from '~hooks/useComponentSize'
import { functions } from '~providers/firebase'
import { ApplicationState } from '~redux'
import { setChatImage } from '~redux/chat/actions'
import { setChatImageLoading } from '~redux/chat/chatSlice'
import { useTheme } from '~theme/ThemeManager'
import fetchAuthenticated from '~utils/fetch'
import urlInfo from '~utils/urlInfo'

type InternalProps = {
  target: string
}

export default function MediaSourceScreen(): React.JSX.Element {
  const { target } = useLocalSearchParams<InternalProps>()

  return <CommunitySource target={target} />
}

function CommunitySource({ target }: InternalProps): React.JSX.Element {
  const { theme } = useTheme()
  const styles = useStyles(theme)
  const [tags, setTags] = useState<Tag[]>([])
  const [query, setQuery] = useState('')
  const throttled = useRef(throttle(setQuery, 500))
  const adjustedColumns = useColumns(1.25)
  const dispatch = useDispatch()
  const { chat } = useSelector((state: ApplicationState) => state.chat)
  const chatRouteParams = useChatRouteParams()
  const router = useRouter()
  const navigation = useNavigation()
  const [listSize, onLayout] = useComponentSize()
  const [dismissed, setDismissed] = useState(false)

  const getFetchUrl = useCallback(() => {
    const args = new URLSearchParams()
    args.append('query', query)
    tags.forEach((tag) => args.append('tags[]', tag.value as string))

    return `/api/v1/images/community?${args.toString()}`
  }, [query, tags])

  const { data, error, isLoading, isValidating, mutate, size, setSize } =
    useSWRInfinite<CommunityImageSearchResourceCollection>(
      (pageIndex, previousPageData) => {
        if (pageIndex === 0) {
          return getFetchUrl
        }

        if (!previousPageData || previousPageData.data.length === 0) return null

        const next = previousPageData?.links?.next

        return next ? next : null
      },
      async (url: string) => {
        const response = await fetchAuthenticated<CommunityImageSearchResourceCollection>(url)
        return response
      },
      {
        revalidateOnFocus: false,
        revalidateFirstPage: false,
      },
    )

  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')

  const suggestions: Tag[] = Object.entries(
    (Array.isArray(data) ? data : []).at(-1)?.tags ?? [],
  ).map(([key, value]) => ({
    label: `${key} (${value})`,
    value: key,
  }))

  const items = (Array.isArray(data) ? data : [])
    .flatMap((o) => o.data)
    .filter((item, index, array) => array.findIndex((o) => o.id === item.id) === index)
  const more = Boolean(data?.at(-1)?.links?.next)

  const processInternetImage = async (
    index: number,
    resource: NonNullable<Pick<SearchEngineImageSearchResource, 'url' | 'width' | 'height'>>,
  ) => {
    const {
      layout: { rows, columns },
    } = chat
    let url = resource.url

    const isManipulated = new RegExp(
      functions.app.options.projectId + '.cloudfunctions.net$',
      'i',
    ).test(urlInfo(url).hostname)

    const maxDimension =
      columns === 1 && rows === 1
        ? IMAGE_MAX_DIMENSION_ONE_SECTION
        : IMAGE_MAX_DIMENSION_OVER_ONE_SECTION

    if (isManipulated) {
      // Append the width and height query args
      url += `&width=${maxDimension}&height=${maxDimension}`
    }

    try {
      dispatch(
        setChatImageLoading({ chat: { id: chat.id, path: chat.path }, index, loading: true }),
      )

      const download =
        Platform.OS === 'web'
          ? url
          : await Asset.fromURI(url)
              .downloadAsync()
              .then((res) => res.localUri)

      if (download) {
        // Persist it
        dispatch(
          setChatImage({
            chat: { id: chat.id, path: chat.path },
            index,
            uri: download,
          }),
        )
      }
    } catch (error) {
      console.warn(error)
    }
  }

  const itemPressed = async (item: CommunityImageSearchResource) => {
    if (!chat) {
      return
    }

    await processInternetImage(Number.parseInt(target), item)

    router.dismissTo(chatRouteParams)
  }

  const renderItem = ({
    index,
    item,
    target,
    extraData,
  }: ListRenderItemInfo<CommunityImageSearchResource>) => {
    return (
      <CommunityImageSearchItem
        width={listSize.width / adjustedColumns}
        item={item}
        onPress={() => itemPressed(item)}
      />
    )
  }

  const handleQueryChanged = (query: string) => {
    throttled.current(query)

    setSize(0)
    mutate()
    setSize(1)
  }

  const onAdd = useCallback(
    (tag: Tag) => {
      setTags((tags) => [...tags, tag])
      setSize(0)
      mutate()
      setSize(1)
    },
    [mutate, setSize],
  )

  const onDelete = useCallback(
    (index: number) => {
      setTags((tags) => tags.filter((_, i) => i !== index))
      mutate()
      setSize(1)
    },
    [mutate, setSize],
  )

  const onEndReached = () => {
    if (more && !isLoadingMore && !isValidating && !dismissed && listSize.height > 0) {
      setSize((s) => s + 1)
    }
  }

  useEffect(() => {
    if (!chat.path) {
      setDismissed(true)
      router.dismissTo(chatRouteParams)
    }
  }, [chat.path, chatRouteParams, router])

  const renderHeader = (
    <View style={styles.content}>
      <TagsInput
        onAdd={onAdd}
        onDelete={onDelete}
        suggestions={suggestions}
        selected={tags}
        onInput={handleQueryChanged}
        splitWords={false}
        minLength={0}
        maxLength={255}
        prefixAdd=""
        maxSuggestions={8}
        prompt="Search for tags"
        searchOnly
      />
    </View>
  )

  const renderEmptyList =
    tags.length === 0 ? null : isValidating ? (
      <View style={styles.emptyContainer}>
        <Loading />
      </View>
    ) : error ? (
      <View style={styles.emptyContainer}>
        <RenderError title="Network Error" description={error.message} />
      </View>
    ) : (
      <View style={styles.emptyContainer}>
        <RenderError title="Oops, nothing here!" description={`No images have been found.`} />
      </View>
    )

  return (
    <ModalScreen ContainerComponent={React.Fragment} closable={false}>
      <View style={styles.inner}>
        <ModalHeader>
          <View style={styles.content}>
            <View style={styles.titleContainer}>
              <View style={styles.header}>
                <Text style={styles.title}>Chatta Community</Text>
                <TouchableOpacity onPress={navigation.goBack}>
                  <FontAwesomeIcon icon={['fal', 'times-circle']} size={ms(24)} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ModalHeader>

        <FlashList<CommunityImageSearchResource>
          onLayout={onLayout}
          key={`community-image-search-${adjustedColumns}`}
          contentContainerStyle={styles.listContainer}
          ListHeaderComponent={renderHeader}
          data={items}
          numColumns={adjustedColumns}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={renderEmptyList}
          masonry
        />
      </View>
    </ModalScreen>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    container: {
      backgroundColor: theme[100],
      flexGrow: 0,
      width: Math.min(Dimensions.get('window').width * 0.8, ms(256)),
    },
    emptyContainer: {
      paddingHorizontal: '10@ms',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    listContainer: {
      backgroundColor: theme[100],
      paddingHorizontal: '10@mvs0.8',
      paddingVertical: '10@mvs0.8',
      minHeight: Dimensions.get('window').height,
    },
    content: {
      flexGrow: 1,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    titleContainer: {
      marginBottom: '10@mvs0.8',
    },
    title: {
      fontSize: '18@ms',
      fontWeight: '700',
      textAlign: 'center',
    },
    subtitle: {
      fontSize: '12@ms0.4',
      fontWeight: '300',
      textAlign: 'center',
    },
    formInput: {
      backgroundColor: theme.white,
    },
    tagContainer: {
      marginEnd: '4@ms',
      marginVertical: '2@ms',
    },
    tag: {
      backgroundColor: theme[400],
      paddingVertical: '6@mvs',
      paddingHorizontal: '4@ms',
    },
    tagActive: {
      backgroundColor: theme.primary,
    },
    tagTitle: {
      fontSize: '10@ms0.2',
      color: theme.white,
    },
    tagContainerStyle: {
      marginBottom: '16@mvs',
    },
    itemSeparator: {
      height: '5@mvs0.8',
    },
  })
}
